import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  applyApiFieldErrors,
  ButtonType,
  ContatoList,
  IconPaths,
  IconSize,
  MaskType,
  ModalSize,
  OptionsHelper,
  Permission,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  ButtonIcon,
  CustomAccordion,
  FetchState,
  FormInput,
  FormRadio,
  FormSelect,
  FormTextArea,
  InfoAlert,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { unidadeSchema } from './validation/unidadeSchema'
import { UnidadeDtoCreate, UnidadeDtoUpdate, UnidadeView } from '../../core/_models'
import { createUnidade, getTiposUnidades, updateUnidade } from '../../core/_requests'
import { useAuth } from '../../../../../auth'
import {
  MSG_SEM_PERMISSAO_UNIDADE_UPDATE,
  usePermissaoUnidadeUpdate,
} from '../../core/_unidadePermissao'
import { TiposUnidadeModal } from './TiposUnidadeModal'
import { getEmpresaById } from '../../../../empresa/empresas-list/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'

export const UnidadeModal: FC = () => {
  const { fetchStates, closeModal, showModal } = useModal()
  const { currentUser } = useAuth()
  const { t } = useT()

  const { data: unidade } = (fetchStates['unidade'] || {}) as FetchState<UnidadeView>
  const { data: contato } = (fetchStates['contato'] || {}) as FetchState<ContatoList>

  const [optionsEspecialidades, setOptionsEspecialidades] = useState<IOption<number>[] | undefined>(
    undefined
  )

  const initialValues = {
    codigo: unidade?.codigo || '',
    nome: unidade?.nome || '',
    telefoneContato: contato?.telefoneContato || '',
    emailContato: contato?.email || '',
    empresa: unidade?.empresa
      ? { label: unidade?.empresa.nome, value: Number(unidade.empresa.id) }
      : (undefined as IOption<number> | undefined),
    ativa: unidade
      ? { label: unidade.ativa ? 'Ativo' : 'Inativo', value: unidade.ativa }
      : (undefined as IOption<boolean> | undefined),

    especialidades: unidade?.especialidadesPermitidas
      ? OptionsHelper.createOptions<number>(unidade.especialidadesPermitidas, 'nome', 'id')
      : (undefined as IOption<number>[] | undefined),

    cnes: unidade?.cnes || '',
    nomeCnes: unidade?.nomeCnes || '',
    codigoIbge: unidade?.codigoIbge || '',
    sus: !!unidade?.sus,
    ceae: !!unidade?.ceae,
    tipoUnidade: unidade?.tipo
      ? { label: unidade.tipo.nome, value: unidade.tipo.id }
      : (undefined as IOption<number> | undefined),
    grupoTelegram: unidade?.grupoTelegram || '',
    observacoes: unidade?.observacoes || [],
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => unidadeSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const especialidades = formik.values.especialidades?.map(
        (especialidade) => especialidade.value
      )
      try {
        if (unidade) {
          const newUnidade: UnidadeDtoUpdate = {
            nome: values.nome,
            codigo: values.codigo,
            ativa: values.ativa?.value as boolean,
            emailContato: values.emailContato,
            telefoneContato: values.telefoneContato || undefined,
            cnes: values.cnes,
            nomeCnes: values.nomeCnes,
            codigoIbge: values.codigoIbge || undefined,
            sus: values.sus,
            ceae: values.ceae,
            tipo: values.tipoUnidade?.value,
            grupoTelegram: values.grupoTelegram,
            especialidades,
            observacoes: values.observacoes,
          }
          await updateUnidade(unidade.id, newUnidade)
        } else {
          const newUnidade: UnidadeDtoCreate = {
            nome: values.nome,
            codigo: values.codigo,
            emailContato: values.emailContato,
            telefoneContato: values.telefoneContato,
            cnes: values.cnes,
            nomeCnes: values.nomeCnes,
            codigoIbge: values.codigoIbge || undefined,
            sus: values.sus,
            ceae: values.ceae,
            tipo: Number(values.tipoUnidade?.value),
            grupoTelegram: values.grupoTelegram,
            especialidades,
            observacoes: values.observacoes,
          }
          await createUnidade(Number(values.empresa?.value), newUnidade)
        }

        await showAlert({
          title: 'Sucesso!',
          text: `Unidade/Setor ${unidade ? 'editada' : 'cadastrada'} com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        const temOutroErro = applyApiFieldErrors(error, formik.setFieldError, ['cnes'])
        if (temOutroErro) {
          showError(error)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const isAdmin = currentUser?.grupo?.nome === Permission.SuperAdmin

  // `isAdmin` (apenas SUPER_ADMIN) gateia o atalho de gerenciar tipos de unidade e é mais restrito
  // que `Unidade:update`; por isso o código IBGE usa o gate próprio da permissão de gravação.
  const podeEditarUnidade = usePermissaoUnidadeUpdate()

  const openTiposModal = () => {
    showModal({
      title: 'Gerenciar Tipos de Unidade',
      size: ModalSize.Medium,
      content: <TiposUnidadeModal />,
      fetchers: {
        tiposUnidades: () => getTiposUnidades(),
      },
    })
  }

  const handleDeleteObservacoes = (id: number) => {
    const novas = formik.values.observacoes.filter((_, i) => i !== id)
    formik.setFieldValue('observacoes', novas)
  }

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const empresa = await getEmpresaById(formik.values.empresa?.value)
        if (empresa.especialidadesPermitidas && empresa.especialidadesPermitidas?.length > 0)
          setOptionsEspecialidades(
            OptionsHelper.createOptions(empresa.especialidadesPermitidas, 'nome', 'id')
          )
      } catch (error) {
        showError(error)
      }
    }

    if (formik.values.empresa?.value) fetchEmpresa()
  }, [formik.values.empresa])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {!unidade && (
        <FormSelect
          formik={formik}
          name='empresa'
          entity='empresa'
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />
      )}

      <div className='d-flex align-items-end mb-3'>
        <FormSelect
          formik={formik}
          name='tipoUnidade'
          label='Tipo da Unidade'
          fetchOptions={OptionsHelper.getOptionsTiposUnidades}
          className='flex-grow-1'
          isDisabled={formik.isSubmitting}
          placeholder='Selecione um tipo de unidade'
          isRequired
        />
        {isAdmin && (
          <ButtonIcon
            icon={IconPaths.Gear}
            iconSize={IconSize.Small}
            onClick={openTiposModal}
            disabled={formik.isSubmitting}
          />
        )}
      </div>
      <FormSelect
        key={optionsEspecialidades ? 'sync' : 'async'}
        formik={formik}
        name='especialidades'
        label='Especialidades'
        placeholder='Selecione especialidades (ou deixe vazio para todas)'
        fetchOptions={optionsEspecialidades ? undefined : OptionsHelper.getOptionsEspecialidades}
        options={optionsEspecialidades}
        isDisabled={formik.isSubmitting}
        infoText='Se não for informado, todas as especialidades estarão disponíveis.'
        className='mb-3'
        isMulti
        highlight
      />

      <div className='row mb-3'>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='codigo'
            placeholder='Digite o código'
            label='Código'
            isRequired
          />
        </div>
        <div className='col-md-8'>
          <FormInput
            formik={formik}
            name='nome'
            placeholder='Digite o nome'
            label='Nome'
            isRequired
          />
        </div>
      </div>
      <FormRadio<boolean>
        formik={formik}
        name='sus'
        label='Atendimento realizado pelo SUS?'
        isDisabled={formik.isSubmitting}
        isRequired
        className='mb-3'
        options={[
          { id: 1, descricao: 'Sim', value: true },
          { id: 2, descricao: 'Não', value: false },
        ]}
      />
      <FormRadio<boolean>
        formik={formik}
        name='ceae'
        label='Unidade CEAE (Centro Estadual de Atenção Especializada)?'
        isDisabled={formik.isSubmitting}
        className='mb-3'
        options={[
          { id: 1, descricao: 'Sim', value: true },
          { id: 2, descricao: 'Não', value: false },
        ]}
      />
      <div className='row mb-3'>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='nomeCnes'
            placeholder='Digite o nome do CNES'
            label='Nome do CNES'
            infoText='Cadastro Nacional de Estabelecimentos de Saúde'
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='cnes'
            placeholder='Digite o CNES'
            label='CNES'
            mask={MaskType.CNES}
            infoText='Cadastro Nacional de Estabelecimentos de Saúde'
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='codigoIbge'
            placeholder='Digite o código IBGE (7 dígitos)'
            label='Código IBGE'
            isDisabled={!podeEditarUnidade}
            infoText={
              podeEditarUnidade
                ? 'Código IBGE do município da unidade (7 dígitos)'
                : `Código IBGE do município da unidade (7 dígitos). ${MSG_SEM_PERMISSAO_UNIDADE_UPDATE}`
            }
          />
        </div>
      </div>

      {unidade && (
        <FormSelect
          formik={formik}
          name='ativa'
          placeholder='Selecione o status'
          label='Status'
          options={OptionsHelper.statusCadastro}
          isClearable={false}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />
      )}
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='emailContato'
            placeholder='Digite o e-mail para contato'
            label='E-mail'
            infoText='E-mail utilizado para contato sobre agendamentos com o paciente'
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='telefoneContato'
            placeholder='Digite o telefone para contato'
            mask={MaskType.Phone}
            label='Telefone'
            infoText='Telefone utilizado para contato sobre agendamentos com o paciente'
            isRequired
          />
        </div>
      </div>
      <FormInput
        className='mb-3'
        formik={formik}
        name='grupoTelegram'
        placeholder='Link do Grupo no Telegram'
        label='Link do Grupo no Telegram'
        infoText='Copie e cole aqui o link do grupo do Telegram'
      />

      <CustomAccordion title='Observações'>
        <div className='row my-2'>
          <h2>Observações</h2>
        </div>
        {formik.values.observacoes.length === 0 && (
          <div className='text-muted fs-7 mb-3'>Nenhuma observação cadastrada</div>
        )}
        {formik.values.observacoes.map((_, index) => (
          <div key={`${index}_observacoes`} className='d-flex align-items-center gap-2 mb-3'>
            <FormTextArea
              formik={formik}
              name={`observacoes.${index}`}
              placeholder='Digite a observação'
              isDisabled={formik.isSubmitting}
              className='w-100'
            />
            <ButtonIcon
              icon={IconPaths.Trash}
              iconSize={IconSize.Small}
              onClick={() => handleDeleteObservacoes(index)}
              className='p-0'
            />
          </div>
        ))}
        <div className='d-flex flex-end'>
          <Button
            className='btn-primary'
            size='btn-sm'
            icon={IconPaths.Add}
            onClick={() => formik.setFieldValue('observacoes', [...formik.values.observacoes, ''])}
          >
            {t('common.add')}
          </Button>
        </div>
      </CustomAccordion>

      <InfoAlert title='Endereços' variant='info' className='mt-5'>
        Os endereços não são mais cadastrados aqui. Use a ação <strong>Listar endereços</strong> no
        menu desta unidade/setor para cadastrar e manter os endereços.
      </InfoAlert>

      <div className='d-flex justify-content-center mt-5'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
